import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "../../common.js";

export const createApprTemp2 = async (apprData, startPage) => {
    const style = apprData.style || {};
    const sections = [];

    const SPACER_HEIGHT = 100;
    const BODY_HEIGHT = 14570;

    const MAX_COL_HEIGHT = BODY_HEIGHT - 200;
    const pointMultiplier = 21;
    const HEADING_EST_HEIGHT = (style.label?.size || 14) * pointMultiplier + 120;
    const LINE_EST_HEIGHT = (style.text?.size || 11) * pointMultiplier;
    const fontSize = style.fontSize || 22;
    const CHARS_PER_LINE = Math.floor(2700 / fontSize);

    let itemQueue = [...(apprData.questions || [])];
    if (!itemQueue.length) return [];

    let currentAbsPage = startPage || 1;

    while (itemQueue.length > 0) {
        const pageChildren = [];
        let currentPageHeight = 0;

        const createQuestionPart = (q, responseText) => {
            const children = [];
            if (q.heading && !q._isContinuation) {
                children.push(new Paragraph({
                    spacing: { after: 120 },
                    children: [
                        new TextRun({
                            text: q.heading || "",
                            color: fixColor(style.label?.color || "#000000"),
                            size: (style.label?.size || 14) * SCALE,
                            font: style.label?.font,
                            bold: style.label?.bold,
                            italics: style.label?.italic,
                            underline: style.label?.underline,
                        }),
                    ],
                }));
            }
            children.push(new Paragraph({
                spacing: { after: 300 },
                children: [
                    new TextRun({
                        text: responseText || "",
                        color: fixColor(style.text?.color || "#333333"),
                        size: (style.text?.size || 11) * SCALE,
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline,
                    }),
                ],
            }));
            return children;
        };

        // Fill Page Content
        while (itemQueue.length > 0) {
            const q = itemQueue[0];
            const headingH = (q._isContinuation) ? 0 : HEADING_EST_HEIGHT;
            const response = q.response || "";
            const responseLines = Math.ceil(response.length / CHARS_PER_LINE) || 1;
            const responseH = (responseLines * LINE_EST_HEIGHT) + 300;
            const totalItemH = headingH + responseH;

            if (currentPageHeight + totalItemH <= MAX_COL_HEIGHT) {
                pageChildren.push(...createQuestionPart(q, response));
                currentPageHeight += totalItemH;
                itemQueue.shift();
            } else {
                const availableForResponse = MAX_COL_HEIGHT - currentPageHeight - headingH - 300;
                if (availableForResponse > LINE_EST_HEIGHT * 2) {
                    const linesThatFit = Math.floor(availableForResponse / LINE_EST_HEIGHT);
                    const charsThatFit = linesThatFit * CHARS_PER_LINE;

                    let splitIndex = charsThatFit;
                    const lastSpace = response.lastIndexOf(' ', splitIndex);
                    if (lastSpace > charsThatFit * 0.7) splitIndex = lastSpace;

                    const firstPart = response.substring(0, splitIndex).trim();
                    const secondPart = response.substring(splitIndex).trim();

                    if (firstPart) {
                        pageChildren.push(...createQuestionPart(q, firstPart));
                        currentPageHeight += headingH + (linesThatFit * LINE_EST_HEIGHT) + 300;
                        itemQueue[0] = {
                            ...q,
                            heading: q.heading,
                            response: secondPart,
                            _isContinuation: true
                        };
                    }
                }
                break;
            }
        }

        const headerTable = createCommonHeader(apprData.title, fixColor(style.colorScheme?.header || "#545454"));
        const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

        // BODY TABLE (Single Column)
        const bodyTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            layout: TableLayoutType.FIXED,
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, children: [] }),
                        new TableCell({
                            width: { size: FULL_WIDTH - 200, type: WidthType.DXA },
                            children: pageChildren,
                        }),
                        new TableCell({ width: { size: 3, type: WidthType.PERCENTAGE }, children: [] }),
                    ],
                }),
            ],
        });

        sections.push({
            properties: {
                page: {
                    margin: { top: 0, right: 0, bottom: 0, left: 0 },
                },
            },
            children: [
                headerTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                bodyTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                footerTable
            ],
        });

        currentAbsPage++;
        if (pageChildren.length === 0) break;
    }

    return sections;
};
